"use client";

import { useState, type FormEvent } from "react";
import type { DocumentSignataire } from "@/prisma/generated/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addDocumentSignataire,
  editDocumentSignataire,
} from "@/app/lib/actions/document-signataires";

type FormValues = {
  documentCode: string;
  signerIdentifier: string;
  firstName: string;
  lastName: string;
  versionNote?: string;
};

const DEFAULT_FORM: FormValues = {
  documentCode: "",
  signerIdentifier: "",
  firstName: "",
  lastName: "",
  versionNote: "",
};

function resolveErrorMessage(result: {
  message?: string;
  fieldErrors?: Record<string, string[]> | string;
}) {
  if (result.message) return result.message;
  if (typeof result.fieldErrors === "string") return result.fieldErrors;
  const firstError = Object.values(result.fieldErrors ?? {}).find(
    (messages) => messages && messages.length > 0,
  );
  return firstError?.[0] ?? "Erreur de validation";
}

export default function DocumentSignataireForm({
  signataire,
  open,
  onClose,
  onSuccess,
}: {
  signataire: DocumentSignataire | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isNew = !signataire;
  const [form, setForm] = useState<FormValues>(() =>
    signataire
      ? {
          documentCode: signataire.documentCode,
          signerIdentifier: signataire.signerIdentifier,
          firstName: signataire.firstName,
          lastName: signataire.lastName,
          versionNote: "",
        }
      : DEFAULT_FORM,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (key: keyof FormValues) => (value: unknown) =>
    setForm((previous) => ({ ...previous, [key]: value }) as FormValues);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const payload = {
      ...(isNew ? {} : { id: signataire.id }),
      documentCode: form.documentCode.trim().toUpperCase(),
      signerIdentifier: form.signerIdentifier.trim(),
      firstName: form.firstName.trim().toUpperCase(),
      lastName: form.lastName.trim().toUpperCase(),
      versionNote: form.versionNote || undefined,
      isActive: true,
    };

    const result = isNew
      ? await addDocumentSignataire(payload)
      : await editDocumentSignataire(payload);

    setLoading(false);

    if (!result.success) {
      setError(resolveErrorMessage(result));
      return;
    }

    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? "Nouveau signataire document"
              : `Modifier - ${signataire?.documentCode} - ${signataire?.signerIdentifier}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Document</Label>
              <Input
                value={form.documentCode}
                onChange={(e) =>
                  set("documentCode")(e.target.value.toUpperCase())
                }
                placeholder="FDM"
                required
                maxLength={3}
                className="font-mono tracking-widest uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Identifiant signataire</Label>
              <Input
                value={form.signerIdentifier}
                onChange={(e) => set("signerIdentifier")(e.target.value)}
                placeholder="SIGNATAIRE_01"
                required
                maxLength={50}
                className="font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prénom signataire</Label>
              <Input
                value={form.firstName}
                onChange={(e) => set("firstName")(e.target.value)}
                placeholder="FRANCOIS"
                required
                maxLength={50}
              />
            </div>

            <div className="space-y-2">
              <Label>Nom signataire</Label>
              <Input
                value={form.lastName}
                onChange={(e) => set("lastName")(e.target.value)}
                placeholder="DAUDIN"
                required
                maxLength={50}
              />
            </div>
          </div>

          {!isNew ? (
            <div className="space-y-2">
              <Label>
                Note de modification{" "}
                <span className="text-muted-foreground font-normal">
                  (optionnel)
                </span>
              </Label>
              <Input
                value={form.versionNote ?? ""}
                onChange={(e) => set("versionNote")(e.target.value)}
                placeholder="Motif de la modification..."
                disabled={loading}
              />
            </div>
          ) : null}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Enregistrement..." : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
